import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  useSessionPersistence,
  type UserSession,
} from "../../../hooks/useSessionPersistence";
import { apiService } from "../../../utils/api";

type SessionPersistence = ReturnType<typeof useSessionPersistence>;

export interface EFrameData {
  session: SessionPersistence["session"];
  isTaskComplete: SessionPersistence["isTaskComplete"];
  isLoading: boolean;
  error: string | null;
}

interface PlannerSessionResponse {
  success?: boolean;
  data?: unknown;
  updatedAt?: string;
  error?: string;
}

const createEmptyReadOnlySession = (): UserSession => ({
  id: "executor-readonly-session",
  currentStep: "dashboard",
  completedTasks: [],
  taskProgress: {},
  onboardingComplete: false,
  lastSaved: "",
  sessionStarted: "",
  lastActivity: "",
  sectionStartTimes: {},
  transmissionStatus: {},
});

const normalizePlannerSession = (data: unknown): UserSession => {
  const emptySession = createEmptyReadOnlySession();

  if (!data || typeof data !== "object") {
    return emptySession;
  }

  const raw = data as Partial<UserSession>;

  return {
    ...emptySession,
    ...raw,
    completedTasks: Array.isArray(raw.completedTasks)
      ? raw.completedTasks
      : [],
    taskProgress:
      raw.taskProgress && typeof raw.taskProgress === "object"
        ? raw.taskProgress
        : {},
    sectionStartTimes:
      raw.sectionStartTimes && typeof raw.sectionStartTimes === "object"
        ? raw.sectionStartTimes
        : {},
    transmissionStatus:
      raw.transmissionStatus && typeof raw.transmissionStatus === "object"
        ? raw.transmissionStatus
        : {},
  };
};

/**
 * Read-only data adapter for Executor EFrames.
 *
 * When estateId is provided (explicitly or via route param on /executor-plan/),
 * this loads the planner/account holder's saved session through
 * executor-authorized read-only APIs.
 *
 * When on an /executor-plan/ route and no estateId can be resolved, this hook
 * FAILS CLOSED — it returns an empty read-only session and logs a warning.
 * It never falls back to the current user's session on executor routes.
 *
 * When estateId is not provided and not on an executor route, it falls back to
 * the current user's session for backwards compatibility with planner call sites.
 */
export function useEFrameData(estateId?: string | null): EFrameData {
  const location = useLocation();
  const routeParams = useParams<{ estateId?: string }>();

  const {
    session: currentUserSession,
    isTaskComplete: currentUserIsTaskComplete,
  } = useSessionPersistence();

  const isExecutorRoute = location.pathname.startsWith("/executor-plan/");

  // Prefer the explicitly-passed estateId; fall back to route param when on an
  // executor route. This allows frames that don't receive the param as a prop
  // to self-heal now that useEFrameData knows about the route.
  const resolvedEstateId: string | null | undefined = estateId
    ? estateId
    : isExecutorRoute
    ? (routeParams.estateId ?? null)
    : undefined;

  const [plannerSession, setPlannerSession] = useState<UserSession>(
    createEmptyReadOnlySession
  );
  const [isLoadingPlannerSession, setIsLoadingPlannerSession] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadPlannerSession = async () => {
      if (!resolvedEstateId) {
        if (isExecutorRoute) {
          // On an executor route but estateId could not be resolved — fail closed.
          console.warn(
            "[useEFrameData] On executor route but could not resolve estateId. " +
              "Returning empty read-only session to prevent data leakage."
          );
          setPlannerSession(createEmptyReadOnlySession());
          setError("Estate ID not available. Cannot load plan data.");
          setIsLoadingPlannerSession(false);
        } else {
          // Non-executor route without estateId — clear any stale planner session.
          setPlannerSession(createEmptyReadOnlySession());
          setError(null);
          setIsLoadingPlannerSession(false);
        }
        return;
      }

      try {
        setIsLoadingPlannerSession(true);
        setError(null);

        const response = (await apiService.request(
          `/api/settlement/${resolvedEstateId}/planner-session`,
          { method: "GET" }
        )) as PlannerSessionResponse;

        if (response.success && response.data) {
          if (!cancelled) setPlannerSession(normalizePlannerSession(response.data));
        } else {
          if (!cancelled) setPlannerSession(createEmptyReadOnlySession());
        }
      } catch (err) {
        if (cancelled) return;

        console.error("[useEFrameData] Failed to load planner session:", err);
        setPlannerSession(createEmptyReadOnlySession());
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load planner session"
        );
      } finally {
        if (!cancelled) {
          setIsLoadingPlannerSession(false);
        }
      }
    };

    loadPlannerSession();

    return () => {
      cancelled = true;
    };
  }, [resolvedEstateId, isExecutorRoute]);

  const session = useMemo(() => {
    if (resolvedEstateId) {
      // Estate-scoped: always use the fetched planner session.
      return plannerSession;
    }
    if (isExecutorRoute) {
      // Executor route without a resolvable estate — fail closed, never leak
      // the authenticated user's own planner session.
      return createEmptyReadOnlySession();
    }
    // Non-executor planner/back-compat path.
    return currentUserSession;
  }, [resolvedEstateId, isExecutorRoute, plannerSession, currentUserSession]);

  const isTaskComplete = useCallback(
    (taskId: string) => {
      if (resolvedEstateId || isExecutorRoute) {
        return session.completedTasks.includes(taskId);
      }
      return currentUserIsTaskComplete(taskId);
    },
    [resolvedEstateId, isExecutorRoute, currentUserIsTaskComplete, session.completedTasks]
  );

  return {
    session,
    isTaskComplete,
    isLoading: resolvedEstateId ? isLoadingPlannerSession : false,
    error,
  };
}

export default useEFrameData;
